// components/project/ProjectFurnitureTab.js
// Tab Arredi per pagina Dettaglio Progetto - Stile simile a Extra

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Plus, Sofa, Download, Edit, Copy, MoreVertical, 
  Send, CheckCircle, Truck, Package, Clock, XCircle,
  Calendar, Building2, FileText, Euro, AlertTriangle,
  ChevronRight, Eye
} from 'lucide-react';
import { 
  formatCurrency, formatDate, 
  furnitureQuoteStatusLabels, furnitureQuoteStatusColors,
  furniturePaymentStatusLabels, furniturePaymentStatusColors,
  getBadgeClass 
} from '../../lib/utils';

const ProjectFurnitureTab = ({ 
  projectId, 
  user,
  isAdmin,
  isAdministration,
  onCreateNew,
  onEdit,
  FurnitureQuoteFormComponent
}) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Modal states
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedQuoteForOrder, setSelectedQuoteForOrder] = useState(null);
  const [orderForm, setOrderForm] = useState({
    supplier_name: '',
    supplier_order_number: '',
    supplier_order_date: new Date().toISOString().split('T')[0],
    estimated_delivery_date: '',
    order_notes: ''
  });

  // Permessi
  const canManageFurniture = isAdmin || isAdministration || 
    user?.role === 'project_manager' || user?.role === 'furniture_sales';
  const canCreateFurniture = isAdmin || user?.role === 'furniture_sales' || user?.role === 'project_manager';

  // Carica preventivi arredi
  const loadQuotes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/furniture-quotes?project_id=${projectId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setQuotes(data);
      }
    } catch (error) {
      console.error('Error loading furniture quotes:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      loadQuotes();
    }
  }, [projectId, loadQuotes]);

  // Calcola totali per stato
  const calculateTotals = () => {
    const totals = {
      bozza: { count: 0, amount: 0 },
      presentato: { count: 0, amount: 0 },
      confermato: { count: 0, amount: 0 },
      ordinato: { count: 0, amount: 0 },
      merce_pronta: { count: 0, amount: 0 },
      consegnato: { count: 0, amount: 0 },
      chiuso: { count: 0, amount: 0 },
      total: { count: 0, amount: 0 }
    };

    quotes.forEach(q => {
      const status = q.status === 'accettato' ? 'confermato' : q.status;
      const amount = q.grand_total || 0;
      
      if (totals[status]) {
        totals[status].count++;
        totals[status].amount += amount;
      }
      
      if (!['annullato'].includes(status)) {
        totals.total.count++;
        totals.total.amount += amount;
      }
    });

    return totals;
  };

  const totals = calculateTotals();

  // === AZIONI RAPIDE ===
  
  const handlePresent = async (quoteId) => {
    setActionLoading(quoteId);
    try {
      const response = await fetch(`/api/furniture-quotes/${quoteId}/present`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Preventivo presentato');
        loadQuotes();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirm = async (quoteId) => {
    setActionLoading(quoteId);
    try {
      const response = await fetch(`/api/furniture-quotes/${quoteId}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({})
      });
      if (response.ok) {
        toast.success('Preventivo confermato');
        loadQuotes();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkOrdered = async () => {
    if (!selectedQuoteForOrder) return;
    
    setActionLoading(selectedQuoteForOrder.id);
    try {
      const response = await fetch(`/api/furniture-quotes/${selectedQuoteForOrder.id}/mark-ordered`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(orderForm)
      });
      if (response.ok) {
        toast.success('Arredi ordinati');
        setShowOrderModal(false);
        setSelectedQuoteForOrder(null);
        setOrderForm({
          supplier_name: '',
          supplier_order_number: '',
          supplier_order_date: new Date().toISOString().split('T')[0],
          estimated_delivery_date: '',
          order_notes: ''
        });
        loadQuotes();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkReady = async (quoteId) => {
    setActionLoading(quoteId);
    try {
      const response = await fetch(`/api/furniture-quotes/${quoteId}/mark-ready`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({})
      });
      if (response.ok) {
        toast.success('Merce pronta registrata');
        loadQuotes();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkDelivered = async (quoteId) => {
    setActionLoading(quoteId);
    try {
      const response = await fetch(`/api/furniture-quotes/${quoteId}/mark-delivered`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({})
      });
      if (response.ok) {
        toast.success('Arredi consegnati');
        loadQuotes();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setActionLoading(null);
    }
  };

  const handleClose = async (quoteId) => {
    setActionLoading(quoteId);
    try {
      const response = await fetch(`/api/furniture-quotes/${quoteId}/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({})
      });
      if (response.ok) {
        toast.success('Preventivo chiuso');
        loadQuotes();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (quoteId) => {
    if (!confirm('Sei sicuro di voler annullare questo preventivo?')) return;
    
    setActionLoading(quoteId);
    try {
      const response = await fetch(`/api/furniture-quotes/${quoteId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: 'Annullato da utente' })
      });
      if (response.ok) {
        toast.success('Preventivo annullato');
        loadQuotes();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAccontoPaid = async (quoteId) => {
    setActionLoading(quoteId);
    try {
      const response = await fetch(`/api/furniture-quotes/${quoteId}/mark-acconto-paid`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Acconto registrato');
        loadQuotes();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaldoPaid = async (quoteId) => {
    setActionLoading(quoteId);
    try {
      const response = await fetch(`/api/furniture-quotes/${quoteId}/mark-saldo-paid`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Saldo registrato');
        loadQuotes();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadPDF = async (quote) => {
    try {
      const response = await fetch(`/api/furniture-quotes/${quote.id}/pdf`, {
        credentials: 'include'
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Preventivo_Arredi_${quote.quote_number?.replace('/', '-') || 'download'}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        toast.error('Errore download PDF');
      }
    } catch (error) {
      toast.error('Errore download PDF');
    }
  };

  const handleDuplicate = async (quoteId) => {
    try {
      const response = await fetch(`/api/furniture-quotes/${quoteId}/duplicate`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Preventivo duplicato');
        loadQuotes();
      } else {
        toast.error('Errore duplicazione');
      }
    } catch (error) {
      toast.error('Errore duplicazione');
    }
  };

  // Helper per azioni disponibili in base allo stato
  const getAvailableActions = (quote) => {
    const status = quote.status;
    const paymentStatus = quote.payment_status;
    const actions = [];

    if (status === 'bozza') {
      actions.push({ key: 'present', label: 'Presenta', icon: Send, handler: () => handlePresent(quote.id) });
      actions.push({ key: 'confirm', label: 'Conferma', icon: CheckCircle, handler: () => handleConfirm(quote.id) });
    }
    
    if (status === 'presentato') {
      actions.push({ key: 'confirm', label: 'Conferma', icon: CheckCircle, handler: () => handleConfirm(quote.id) });
    }
    
    if (status === 'confermato' || status === 'accettato') {
      if (paymentStatus !== 'acconto_pagato' && (isAdmin || isAdministration)) {
        actions.push({ key: 'acconto', label: 'Registra acconto', icon: Euro, handler: () => handleAccontoPaid(quote.id) });
      }
      if (paymentStatus === 'acconto_pagato' || isAdmin) {
        actions.push({ 
          key: 'order', 
          label: 'Segna ordinato', 
          icon: Package, 
          handler: () => {
            setSelectedQuoteForOrder(quote);
            setOrderForm(prev => ({
              ...prev,
              supplier_name: quote.supplier_name || '',
              supplier_order_number: quote.supplier_order_number || ''
            }));
            setShowOrderModal(true);
          }
        });
      }
    }
    
    if (status === 'ordinato') {
      actions.push({ key: 'ready', label: 'Merce pronta', icon: Package, handler: () => handleMarkReady(quote.id) });
      actions.push({ key: 'delivered', label: 'Consegnato', icon: Truck, handler: () => handleMarkDelivered(quote.id) });
    }
    
    if (status === 'merce_pronta') {
      if (paymentStatus !== 'saldo_pagato' && (isAdmin || isAdministration)) {
        actions.push({ key: 'saldo', label: 'Registra saldo', icon: Euro, handler: () => handleSaldoPaid(quote.id) });
      }
      actions.push({ key: 'delivered', label: 'Consegnato', icon: Truck, handler: () => handleMarkDelivered(quote.id) });
    }
    
    if (status === 'consegnato') {
      if (paymentStatus !== 'saldo_pagato' && (isAdmin || isAdministration)) {
        actions.push({ key: 'saldo', label: 'Registra saldo', icon: Euro, handler: () => handleSaldoPaid(quote.id) });
      }
      if (isAdmin || user?.role === 'project_manager') {
        actions.push({ key: 'close', label: 'Chiudi', icon: CheckCircle, handler: () => handleClose(quote.id) });
      }
    }
    
    if (!['chiuso', 'annullato'].includes(status) && (isAdmin || user?.role === 'project_manager')) {
      actions.push({ key: 'cancel', label: 'Annulla', icon: XCircle, handler: () => handleCancel(quote.id), danger: true });
    }

    return actions;
  };

  // Render status badge
  const renderStatusBadge = (status) => {
    const displayStatus = status === 'accettato' ? 'confermato' : status;
    return (
      <span className={getBadgeClass(furnitureQuoteStatusColors[displayStatus])}>
        {furnitureQuoteStatusLabels[displayStatus] || displayStatus}
      </span>
    );
  };

  // Render payment status
  const renderPaymentStatus = (paymentStatus) => {
    return (
      <span className={`text-xs px-2 py-0.5 rounded ${
        paymentStatus === 'saldo_pagato' ? 'bg-green-100 text-green-700' :
        paymentStatus === 'acconto_pagato' ? 'bg-blue-100 text-blue-700' :
        'bg-amber-100 text-amber-700'
      }`}>
        {furniturePaymentStatusLabels[paymentStatus] || paymentStatus}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <p className="text-[#737373]">Caricamento arredi...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="project-furniture-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-h4">Preventivi Arredi</h3>
          <p className="text-sm text-[#737373]">Gestisci la vendita di arredi per questo progetto</p>
        </div>
        {canCreateFurniture && (
          <Button onClick={onCreateNew} data-testid="new-furniture-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Preventivo
          </Button>
        )}
      </div>

      {/* Summary Cards - Stile Extra */}
      {quotes.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
          {/* Totale Arredi */}
          <Card className="card-abitalo bg-gradient-to-br from-purple-100 to-purple-50 col-span-2 sm:col-span-1">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Sofa className="w-4 h-4 text-purple-600" />
                <div>
                  <p className="text-xs text-purple-600 font-medium">Totale Arredi</p>
                  <p className="font-bold text-purple-700">{formatCurrency(totals.total.amount)}</p>
                  <p className="text-xs text-purple-500">{totals.total.count} preventivi</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bozze */}
          <Card className="card-abitalo">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500">Bozze</p>
              <p className="font-semibold">{totals.bozza.count}</p>
              <p className="text-xs text-gray-400">{formatCurrency(totals.bozza.amount)}</p>
            </CardContent>
          </Card>

          {/* Presentati */}
          <Card className="card-abitalo">
            <CardContent className="p-3">
              <p className="text-xs text-blue-500">Presentati</p>
              <p className="font-semibold text-blue-600">{totals.presentato.count}</p>
              <p className="text-xs text-blue-400">{formatCurrency(totals.presentato.amount)}</p>
            </CardContent>
          </Card>

          {/* Confermati */}
          <Card className="card-abitalo">
            <CardContent className="p-3">
              <p className="text-xs text-green-500">Confermati</p>
              <p className="font-semibold text-green-600">{totals.confermato.count}</p>
              <p className="text-xs text-green-400">{formatCurrency(totals.confermato.amount)}</p>
            </CardContent>
          </Card>

          {/* Ordinati */}
          <Card className="card-abitalo">
            <CardContent className="p-3">
              <p className="text-xs text-amber-500">Ordinati</p>
              <p className="font-semibold text-amber-600">{totals.ordinato.count}</p>
              <p className="text-xs text-amber-400">{formatCurrency(totals.ordinato.amount)}</p>
            </CardContent>
          </Card>

          {/* Merce Pronta */}
          <Card className="card-abitalo">
            <CardContent className="p-3">
              <p className="text-xs text-orange-500">Merce pronta</p>
              <p className="font-semibold text-orange-600">{totals.merce_pronta.count}</p>
              <p className="text-xs text-orange-400">{formatCurrency(totals.merce_pronta.amount)}</p>
            </CardContent>
          </Card>

          {/* Consegnati */}
          <Card className="card-abitalo">
            <CardContent className="p-3">
              <p className="text-xs text-teal-500">Consegnati</p>
              <p className="font-semibold text-teal-600">{totals.consegnato.count + totals.chiuso.count}</p>
              <p className="text-xs text-teal-400">{formatCurrency(totals.consegnato.amount + totals.chiuso.amount)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Lista Preventivi */}
      {quotes.length === 0 ? (
        <Card className="card-abitalo">
          <CardContent className="p-8 text-center">
            <Sofa className="w-12 h-12 text-[#DCDCDC] mx-auto mb-3" />
            <p className="text-[#737373] mb-4">Nessun preventivo arredi per questo progetto</p>
            {canCreateFurniture && (
              <Button onClick={onCreateNew}>
                <Plus className="w-4 h-4 mr-2" />
                Crea Preventivo
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {quotes.map((quote) => {
            const actions = getAvailableActions(quote);
            const displayStatus = quote.status === 'accettato' ? 'confermato' : quote.status;
            const isLoading = actionLoading === quote.id;

            return (
              <Card key={quote.id} className="card-abitalo" data-testid={`furniture-quote-${quote.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Info principale */}
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        displayStatus === 'bozza' ? 'bg-gray-100' :
                        displayStatus === 'presentato' ? 'bg-blue-100' :
                        displayStatus === 'confermato' ? 'bg-green-100' :
                        displayStatus === 'ordinato' ? 'bg-amber-100' :
                        displayStatus === 'merce_pronta' ? 'bg-orange-100' :
                        displayStatus === 'consegnato' ? 'bg-teal-100' :
                        displayStatus === 'chiuso' ? 'bg-green-100' :
                        'bg-red-100'
                      }`}>
                        <Sofa className={`w-5 h-5 ${
                          displayStatus === 'bozza' ? 'text-gray-500' :
                          displayStatus === 'presentato' ? 'text-blue-500' :
                          displayStatus === 'confermato' ? 'text-green-500' :
                          displayStatus === 'ordinato' ? 'text-amber-500' :
                          displayStatus === 'merce_pronta' ? 'text-orange-500' :
                          displayStatus === 'consegnato' ? 'text-teal-500' :
                          displayStatus === 'chiuso' ? 'text-green-500' :
                          'text-red-500'
                        }`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium">{quote.quote_number}</p>
                          {renderStatusBadge(quote.status)}
                          {renderPaymentStatus(quote.payment_status)}
                        </div>
                        <p className="text-sm text-[#737373] truncate">{quote.title}</p>
                        
                        {/* Info ordine se presente */}
                        {(quote.supplier_name || quote.supplier_order_number) && (
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                            {quote.supplier_name && (
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {quote.supplier_name}
                              </span>
                            )}
                            {quote.supplier_order_number && (
                              <span className="flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                N° {quote.supplier_order_number}
                              </span>
                            )}
                            {quote.estimated_delivery_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Consegna: {formatDate(quote.estimated_delivery_date)}
                              </span>
                            )}
                          </div>
                        )}
                        
                        <p className="text-xs text-[#999] mt-1">
                          Creato il {formatDate(quote.created_at)}
                          {quote.updated_at && quote.updated_at !== quote.created_at && 
                            ` • Agg. ${formatDate(quote.updated_at)}`}
                        </p>
                      </div>
                    </div>

                    {/* Importi e azioni */}
                    <div className="flex items-center gap-4 flex-wrap">
                      {/* Importi */}
                      <div className="text-right">
                        <p className="font-semibold text-[#FF7819]">{formatCurrency(quote.grand_total)}</p>
                        <p className="text-xs text-gray-500">IVA inclusa</p>
                        {quote.acconto_amount > 0 && (
                          <p className="text-xs text-gray-400">
                            Acc. {formatCurrency(quote.acconto_amount)} / Saldo {formatCurrency(quote.saldo_amount)}
                          </p>
                        )}
                      </div>

                      {/* Azioni */}
                      {canManageFurniture && (
                        <div className="flex items-center gap-1">
                          {/* PDF */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownloadPDF(quote)}
                            title="Scarica PDF"
                            className="text-amber-600 hover:bg-amber-50"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          
                          {/* Edit */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onEdit(quote)}
                            title="Modifica"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          
                          {/* Duplicate */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDuplicate(quote.id)}
                            title="Duplica"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>

                          {/* Azioni rapide dropdown */}
                          {actions.length > 0 && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="outline" disabled={isLoading}>
                                  {isLoading ? (
                                    <Clock className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      Azioni
                                      <ChevronRight className="w-4 h-4 ml-1" />
                                    </>
                                  )}
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {actions.map((action, idx) => (
                                  <React.Fragment key={action.key}>
                                    {action.danger && idx > 0 && <DropdownMenuSeparator />}
                                    <DropdownMenuItem 
                                      onClick={action.handler}
                                      className={action.danger ? 'text-red-600' : ''}
                                    >
                                      <action.icon className="w-4 h-4 mr-2" />
                                      {action.label}
                                    </DropdownMenuItem>
                                  </React.Fragment>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Info box */}
      <Card className="card-abitalo bg-purple-50 border-purple-200">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-purple-800">
              <p className="font-medium mb-1">Gestione Arredi</p>
              <p>I preventivi arredi seguono il flusso: Bozza → Presentato → Confermato → Ordinato → Merce pronta → Consegnato → Chiuso.</p>
              <p className="mt-1">Pagamento: 50% acconto all'ordine + 50% saldo a merce pronta.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modal Ordine */}
      <Dialog open={showOrderModal} onOpenChange={setShowOrderModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Segna come Ordinato</DialogTitle>
            <DialogDescription>
              Inserisci i dettagli dell'ordine al fornitore per {selectedQuoteForOrder?.quote_number}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Fornitore *</Label>
              <Input
                value={orderForm.supplier_name}
                onChange={(e) => setOrderForm(prev => ({ ...prev, supplier_name: e.target.value }))}
                placeholder="Nome fornitore"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>N° Ordine</Label>
                <Input
                  value={orderForm.supplier_order_number}
                  onChange={(e) => setOrderForm(prev => ({ ...prev, supplier_order_number: e.target.value }))}
                  placeholder="N° ordine"
                />
              </div>
              <div className="space-y-2">
                <Label>Data Ordine</Label>
                <Input
                  type="date"
                  value={orderForm.supplier_order_date}
                  onChange={(e) => setOrderForm(prev => ({ ...prev, supplier_order_date: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Data Consegna Prevista</Label>
              <Input
                type="date"
                value={orderForm.estimated_delivery_date}
                onChange={(e) => setOrderForm(prev => ({ ...prev, estimated_delivery_date: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Note Ordine</Label>
              <Textarea
                value={orderForm.order_notes}
                onChange={(e) => setOrderForm(prev => ({ ...prev, order_notes: e.target.value }))}
                placeholder="Note aggiuntive..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrderModal(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleMarkOrdered}
              disabled={!orderForm.supplier_name || actionLoading}
            >
              {actionLoading ? 'Salvataggio...' : 'Conferma Ordine'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectFurnitureTab;
